import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import axios from "axios";
import axiosInstance, { BASE_URL } from "../../utils/config/config";
import { getUserById } from "../../redux/actions/user.dispatcher";
import { compressImage } from "../../utils/compressImage";

// Client (patient) self-profile — DB-only, no GHL. Edits Users name/avatar +
// non-clinical Patient fields via GET/PUT /user/patient-profile.
const EMPTY = {
  firstName: "",
  lastName: "",
  email: "",
  avatar: "",
  phone: "",
  gender: "",
  genderPreference: "",
  dob: "",
  state: "",
  language: "",
  sessionType: "",
  personalGoals: "",
};

// dob comes back as an ISO datetime; the date input wants YYYY-MM-DD.
const toDateInput = (v) => (v ? String(v).slice(0, 10) : "");

export const useClientProfile = () => {
  const dispatch = useDispatch();

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

  const notify = useCallback((severity, msg) => setSnack({ open: true, severity, msg }), []);
  const setField = useCallback((k, v) => setForm((f) => ({ ...f, [k]: v })), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get("user/patient-profile");
      setForm({ ...EMPTY, ...data, dob: toDateInput(data.dob) });
    } catch (err) {
      notify("error", err.response?.data?.message || "Failed to load profile");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpload = useCallback(
    async (file) => {
      if (!file) return;
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append("file", await compressImage(file));
        const res = await axios.post(`${BASE_URL}aws/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (res.data?.fileUrl) setField("avatar", res.data.fileUrl);
      } catch (err) {
        notify("error", err.response?.data?.message || "Upload failed");
      } finally {
        setUploading(false);
      }
    },
    [setField, notify]
  );

  const handleSubmit = useCallback(async () => {
    if (!form.firstName.trim() || !form.lastName.trim()) {
      notify("error", "First and last name are required.");
      return;
    }
    setSaving(true);
    try {
      await axiosInstance.put("user/patient-profile", {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        avatar: form.avatar,
        phone: form.phone,
        gender: form.gender,
        genderPreference: form.genderPreference,
        dob: form.dob || null,
        state: form.state,
        language: form.language,
        sessionType: form.sessionType,
        personalGoals: form.personalGoals,
      });
      dispatch(getUserById());
      notify("success", "Profile saved");
    } catch (err) {
      notify("error", err.response?.data?.message || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  }, [form, notify, dispatch]);

  const closeSnack = useCallback(() => setSnack((s) => ({ ...s, open: false })), []);

  return { form, loading, saving, uploading, setField, handleUpload, handleSubmit, snack, closeSnack };
};
